import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { LifeBuoy, Sparkles, FileText, BookOpen } from "lucide-react";
import { customerTopics, staffTopics, docsTopics } from "@/lib/help-content";
import { HelpTopicGrid } from "@/components/help/HelpTopicGrid";
import { HelpSearch } from "@/components/help/HelpSearch";
import { APP_VERSION, CHANGELOG } from "@/lib/app-version";

export const Route = createFileRoute("/app/help")({
  component: AppHelp,
});

function AppHelp() {
  const { profile } = useAuth();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";

  const searchTopics = [
    ...customerTopics.map((t) => ({
      ...t,
      sectionLabel: "Customers",
      sectionLink: "/app/help",
    })),
    ...(isStaff
      ? [
          ...staffTopics.map((t) => ({
            ...t,
            sectionLabel: "Staff",
            sectionLink: "/app/help",
          })),
          ...docsTopics.map((t) => ({
            ...t,
            sectionLabel: "Docs",
            sectionLink: "/app/help",
          })),
        ]
      : []),
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 space-y-8 sm:space-y-10">
      <header className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-hero text-primary-foreground">
          <LifeBuoy className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold">Help center</h1>
            <span className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground">
              v{APP_VERSION}
            </span>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {isStaff
              ? "Quick guides for running the day, editing safely, and managing the catalogue."
              : "Everything you need to submit, track, and reorder jobs."}
          </p>
        </div>
      </header>

      <HelpSearch topics={searchTopics} />

      <section aria-labelledby="whatsnew-heading" className="space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 id="whatsnew-heading" className="text-xl font-semibold">
            What&apos;s new
          </h2>
        </div>
        <ol className="space-y-3">
          {CHANGELOG.map((entry, idx) => (
            <li
              key={entry.version}
              className="rounded-3xl border border-border bg-card p-4 sm:p-5 shadow-soft"
            >
              <div className="flex flex-wrap items-baseline gap-2 mb-2">
                <span className="text-sm font-semibold">v{entry.version}</span>
                <time className="text-xs text-muted-foreground" dateTime={entry.date}>
                  {new Date(entry.date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </time>
                {idx === 0 && (
                  <span className="rounded-full bg-primary/10 text-primary text-[10px] font-medium px-2 py-0.5 uppercase tracking-wide">
                    Current
                  </span>
                )}
              </div>
              <ul className="list-disc list-outside pl-5 space-y-1 text-sm text-muted-foreground marker:text-muted-foreground/60">
                {entry.highlights.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="customer-guides-heading" className="space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h2 id="customer-guides-heading" className="text-xl font-semibold">
            Customer guides
          </h2>
        </div>
        <HelpTopicGrid topics={customerTopics} />
      </section>

      {isStaff && (
        <section aria-labelledby="staff-guides-heading" className="space-y-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 id="staff-guides-heading" className="text-xl font-semibold">
              Staff guides
            </h2>
          </div>
          <HelpTopicGrid topics={staffTopics} />
        </section>
      )}

      {isStaff && (
        <section aria-labelledby="docs-heading" className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h2 id="docs-heading" className="text-xl font-semibold">
              Technical documentation
            </h2>
          </div>
          <HelpTopicGrid topics={docsTopics} />
        </section>
      )}

      <section className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold mb-2">Pricing & artwork guidelines</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Live trade pricing, cutting tiers, and artwork requirements.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-hero px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-soft hover:opacity-90"
        >
          View pricing
        </Link>
      </section>
    </div>
  );
}
